import React, { useState, useEffect } from 'react';
import { BarChart3, LineChart, AreaChart, PieChart, TrendingUp, Shield, Zap, Settings, ChevronDown, ChevronUp, EyeOff, Eye, DollarSign, Activity, Calendar, Percent, RefreshCw, Play, Pause, RotateCcw, SkipForward, SkipBack } from 'lucide-react';
import { formatCurrency, shortenLargeNumber } from '../../utils/formatters';
import { Investment, InvestmentResults, ChartVisibilityControls, ChartVisibilityResetFunctions } from '../../types';
import { ChartDisplayType } from '../../types';
import ModernLineChart from './ModernLineChart';
import ModernBarChart from './BarChart';
import ModernAreaChart from './ModernAreaChart';
import ModernPieChart from './PieChart';
import ChartTypeSelector from '../ui/ChartTypeSelector';
import MultiplierCard from './MultiplierCard';
import { Toggle } from '../ui/Toggle';
import ChartSeriesToggleCard from '../ui/ChartSeriesToggleCard';
import ChartSeriesToggleCard2 from '../ui/ChartSeriesToggleCard2';
import ChartSeriesToggleCard3 from '../ui/ChartSeriesToggleCard3';
import AnimatedNumberDisplay from '../ui/AnimatedNumberDisplay';

interface ChartCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  availableTypes?: ChartDisplayType[];
  yLabelFormatter?: (value: number) => string;
}

interface ChartElement {
  dataKey: string;
  name: string;
  color: string;
  strokeWidth?: number;
  fillOpacity?: number;
  strokeDasharray?: string;
}

interface PieDataItem {
  name: string;
  value: number;
  color: string;
}

interface DynamicChartSystemProps {
  title?: string;
  categories: ChartCategory[];
  chartData: Record<string, any[]>;
  chartElements: Record<string, ChartElement[]>;
  pieData?: Record<string, PieDataItem[]>;
  formatters: {
    yLabel: (value: number) => string;
    tooltip: (value: number, name?: string) => string;
  };
  summaryData?: Record<string, Array<{
    label: string;
    value: string;
    color: string;
  }>>;
  additionalInfo?: Record<string, React.ReactNode>;
  investment?: Investment;
  multiplierValidChartIds?: string[];
  hideAdditionalOptions?: boolean;
  visibilityControls?: ChartVisibilityControls;
  resetVisibilityFunctions?: ChartVisibilityResetFunctions;
}

const DynamicChartSystem: React.FC<DynamicChartSystemProps> = ({
  title,
  categories = [],
  chartData = {},
  chartElements = {},
  pieData = {},
  formatters,
  summaryData = {},
  additionalInfo = {},
  investment,
  multiplierValidChartIds,
  hideAdditionalOptions = false,
  visibilityControls,
  resetVisibilityFunctions,
}) => {
  const [activeChart, setActiveChart] = useState<string>(categories.length > 0 ? categories[0].id : '');
  const [chartTypes, setChartTypes] = useState<Record<string, ChartDisplayType>>(() => {
    const initialTypes: Record<string, ChartDisplayType> = {};
    categories.forEach((category, index) => {
      const defaultTypes: ChartDisplayType[] = ['line', 'bar', 'area'];
      initialTypes[category.id] = defaultTypes[index % defaultTypes.length];
    });
    return initialTypes;
  });
  const [toggleCardStyle, setToggleCardStyle] = useState<1 | 2 | 3>(1);

  const handleChartTypeChange = (categoryId: string, type: ChartDisplayType) => {
    setChartTypes(prev => ({
      ...prev,
      [categoryId]: type
    }));
  };

  const renderChart = (categoryId: string) => {
    const chartType = chartTypes[categoryId];
    const data = chartData[categoryId];
    const elements = chartElements[categoryId];
    const activeCategory = categories.find(c => c.id === categoryId);
    
    if (!data || !elements) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-gray-500 mb-2">No hay datos disponibles para mostrar</p>
            <p className="text-sm text-gray-400">Verifica la configuración o intenta con otros parámetros</p>
          </div>
        </div>
      );
    }

    const formatYLabel = activeCategory?.yLabelFormatter || formatters.yLabel;

    const commonProps = {
      data: data,
      xAxisDataKey: "year",
      formatYLabel: formatYLabel,
      formatTooltip: (value: number, name: string) => formatters.tooltip(value, name)
    };

    switch (chartType) {
      case 'line':
        return (
          <ModernLineChart
            {...commonProps}
            lines={elements}
          />
        );
      case 'bar':
        return (
          <ModernBarChart
            {...commonProps}
            bars={elements}
          />
        );
      case 'area':
        return (
          <ModernAreaChart
            {...commonProps}
            areas={elements}
            stacked={false}
          />
        );
      case 'pie':
        const pieCategoryData = pieData[categoryId];
        if (!pieCategoryData) return null;
        return (
          <ModernPieChart
            data={pieCategoryData}
            formatValue={(value) => formatters.tooltip(value)}
            formatTooltip={(value, name, percent) => 
              `${formatters.tooltip(value)} (${percent.toFixed(1)}%)`
            }
            innerRadius={80}
            outerRadius={150}
          />
        );
      default:
        return null;
    }
  };

  const activeCategory = categories.find(c => c.id === activeChart);
  const activeVisibilityControls = activeChart && visibilityControls ? 
    visibilityControls[activeChart as keyof ChartVisibilityControls] : undefined;

  const getSeriesData = () => {
    if (!activeChart || !chartElements[activeChart]) return [];
    
    const elements = chartElements[activeChart];
    
    return elements.map(element => {
      const visibilityKey = 
        element.dataKey === 'Cosecha Capital' ? 'cosechaCapital' :
        element.dataKey === 'CETES' ? 'cetes' :
        element.dataKey === 'Ahorro Tradicional' ? 'savings' :
        element.dataKey === 'Bienes Raíces' ? 'realEstate' :
        element.dataKey === 'Retiro Parcial' ? 'showPartialCashOut' :
        element.dataKey === 'Flujo Cosecha Capital' ? 'cosechaCapital' :
        element.dataKey === 'Flujo de Efectivo' ? 'cosechaCapital' :
        element.dataKey === 'Flujo CETES' ? 'cetes' :
        element.dataKey === 'Flujo Ahorro' ? 'savings' :
        element.dataKey === 'Flujo Bienes Raíces' ? 'realEstate' :
        element.dataKey === 'Fondo de Reinversión' ? 'cosechaCapital' :
        element.dataKey === 'Suma Precio y Flujo' ? 'showSummedValue' :
        element.dataKey === 'Capital Aportado' ? 'contributedCapital' :
        element.dataKey === 'Flujo Acumulado' ? 'cumulativeFlow' :
        element.dataKey === 'Porcentaje de Retiro' ? 'showPercentages' :
        element.dataKey === 'Porcentaje de Reinversión' ? 'showPercentages' :
        element.dataKey === 'Monto de Retiro' ? 'showAmounts' :
        element.dataKey === 'Monto de Reinversión' ? 'showAmounts' :
        element.dataKey === 'Utilidad Total' ? 'showAmounts' :
        element.dataKey === 'Retiro Acumulado' ? 'showCumulativeRetirement' :
        element.dataKey === 'Retiro Anual' ? 'showCumulativeRetirement' :
        element.dataKey === 'Reinversión Acumulada' ? 'showCumulativeReinvestment' :
        element.dataKey === 'Reinversión Anual' ? 'showCumulativeReinvestment' :
        element.dataKey === 'Utilidad Total Acumulada' ? 'showCumulativeReinvestment' :
        '';
      
      const isVisible = activeVisibilityControls?.visibility[visibilityKey as keyof typeof activeVisibilityControls.visibility] as boolean;
      const toggleFunction = activeVisibilityControls?.toggle;
      
      if (isVisible !== undefined && toggleFunction && visibilityKey) {
        return {
          dataKey: element.dataKey,
          name: element.name,
          color: element.color,
          isVisible,
          visibilityKey,
          toggle: () => toggleFunction(visibilityKey as any)
        };
      }
      
      return null;
    }).filter(Boolean);
  };

  const seriesData = getSeriesData();

  const handleResetVisibility = () => {
    if (resetVisibilityFunctions && activeChart) {
      const resetFunction = resetVisibilityFunctions[activeChart as keyof typeof resetVisibilityFunctions];
      if (resetFunction) {
        resetFunction();
      }
    }
  };

  if (!categories || categories.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="text-gray-500">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">No hay datos de gráficos disponibles</p>
          <p className="text-sm mt-2">Los gráficos aparecerán aquí una vez que se proporcionen los datos.</p>
        </div>
      </div>
    );
  }

  const renderToggleCard = (item: any, index: number) => {
    const commonProps = {
      key: index,
      label: item.name,
      icon: 
        item.name.includes('Cosecha Capital') ? TrendingUp :
        item.name.includes('CETES') ? Activity :
        item.name.includes('Ahorro') ? DollarSign :
        item.name.includes('Bienes Raíces') ? Calendar :
        item.name.includes('Retiro Parcial') ? Percent :
        item.name.includes('Capital Aportado') ? DollarSign :
        item.name.includes('EBITDA') ? Zap :
        item.name.includes('Retiro') ? Percent :
        item.name.includes('Reinversión') ? RefreshCw :
        item.name.includes('Utilidad') ? Activity :
        TrendingUp,
      color: item.color,
      checked: item.isVisible,
      onClick: item.toggle
    };

    switch (toggleCardStyle) {
      case 1:
        return <ChartSeriesToggleCard {...commonProps} />;
      case 2:
        return <ChartSeriesToggleCard2 {...commonProps} />;
      case 3:
        return <ChartSeriesToggleCard3 {...commonProps} />;
      default:
        return <ChartSeriesToggleCard {...commonProps} />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title || "Análisis de Inversión"}</h3>
        
        <div className={`grid gap-4 ${categories.length <= 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'}`}>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveChart(category.id)}
              className={`p-4 rounded-xl transition-all duration-300 ${
                activeChart === category.id
                  ? `bg-gradient-to-br ${category.color} text-white shadow-lg transform scale-105`
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
              title={category.description}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${
                  activeChart === category.id 
                    ? 'bg-white bg-opacity-20' 
                    : 'bg-gray-200'
                }`}>
                  {category.icon}
                </div>
                <h4 className="font-semibold text-sm">{category.title}</h4>
              </div>
              <p className={`text-xs ${
                activeChart === category.id ? 'text-white text-opacity-90' : 'text-gray-500'
              }`}>
                {category.description}
              </p>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {activeCategory?.title}
            </h3>
            <p className="text-gray-600 text-sm">
              {activeCategory?.description}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
              <button 
                onClick={() => setToggleCardStyle(1)}
                className={`p-1 rounded ${toggleCardStyle === 1 ? 'bg-white shadow' : ''}`}
                title="Estilo 1"
              >
                <div className="w-6 h-6 flex items-center justify-center">1</div>
              </button>
              <button 
                onClick={() => setToggleCardStyle(2)}
                className={`p-1 rounded ${toggleCardStyle === 2 ? 'bg-white shadow' : ''}`}
                title="Estilo 2"
              >
                <div className="w-6 h-6 flex items-center justify-center">2</div>
              </button>
              <button 
                onClick={() => setToggleCardStyle(3)}
                className={`p-1 rounded ${toggleCardStyle === 3 ? 'bg-white shadow' : ''}`}
                title="Estilo 3"
              >
                <div className="w-6 h-6 flex items-center justify-center">3</div>
              </button>
            </div>
            
            <ChartTypeSelector
              selectedType={chartTypes[activeChart]}
              onTypeChange={(type) => handleChartTypeChange(activeChart, type)}
              availableTypes={activeCategory?.availableTypes || ['line', 'bar', 'area', 'pie']}
            />
          </div>
        </div>

        {seriesData.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Series de Datos</h4>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2">
              {seriesData.map((item, index) => renderToggleCard(item, index))}
            </div>
          </div>
        )}

        <div className="h-96">
          {renderChart(activeChart)}
        </div>

        {resetVisibilityFunctions && activeChart && resetVisibilityFunctions[activeChart as keyof typeof resetVisibilityFunctions] && (
          <div className="flex justify-center mt-4">
            <button
              onClick={handleResetVisibility}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              title="Restablecer todas las series de datos"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Restablecer Datos</span>
            </button>
          </div>
        )}

        {summaryData[activeChart] && (
          <div className="mt-4 space-y-4">
            <div className={`grid gap-4 ${summaryData[activeChart].length <= 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5'}`}>
              {summaryData[activeChart].map((item, index) => (
                <div key={index} className={`text-center p-3 rounded-lg ${item.color}`}>
                  <div className="text-sm mb-1">{item.label}</div>
                  <div className="text-lg font-bold">
                    {investment ? (
                      <AnimatedNumberDisplay
                        value={(() => {
                          const numericValue = parseFloat(item.value.replace(/[^0-9.-]/g, '')) || 0;
                          return numericValue;
                        })()}
                        currencyFormat={investment.currencyFormat}
                        duration={1.2}
                        staggerDelay={0.08}
                        isCurrency={!item.label.includes('Certificados') && !item.label.includes('Multiplicador') && !item.label.includes('CAGR') && !item.label.includes('%') && !item.label.includes('Año')}
                      />
                    ) : (
                      item.value
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {summaryData[activeChart] && investment && multiplierValidChartIds?.includes(activeChart) && (
          <MultiplierCard 
            chartId={activeChart}
            summaryData={summaryData[activeChart]}
            currencyFormat={investment.currencyFormat}
            validChartIds={multiplierValidChartIds}
          />
        )}

        {!hideAdditionalOptions && activeVisibilityControls && (
          <div className="mt-4 bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-green-600" />
              <h3 className="text-base font-medium text-gray-800">Opciones Adicionales</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {activeChart === 'patrimony' && activeVisibilityControls && (
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">Ajustes:</div>
                  <div className="space-y-3">
                    <Toggle
                      label="Mostrar Valores Reales"
                      checked={activeVisibilityControls.visibility.showReal}
                      onChange={() => activeVisibilityControls.toggle('showReal')}
                    />
                  </div>
                </div>
              )}

              {activeChart === 'income' && activeVisibilityControls && (
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">Ajustes:</div>
                  <div className="space-y-3">
                    <Toggle
                      label="Mostrar Valores Reales"
                      checked={activeVisibilityControls.visibility.showReal}
                      onChange={() => activeVisibilityControls.toggle('showReal')}
                    />

                    {'showTaxes' in activeVisibilityControls.visibility && (
                      <Toggle
                        label="Mostrar Impuestos"
                        checked={activeVisibilityControls.visibility.showTaxes}
                        onChange={() => activeVisibilityControls.toggle('showTaxes')}
                      />
                    )}
                  </div>
                </div>
              )}

              {activeChart === 'cashflow' && activeVisibilityControls && (
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">Ajustes:</div>
                  <div className="space-y-3">
                    <Toggle
                      label="Mostrar Flujo Acumulado"
                      checked={activeVisibilityControls.visibility.cumulativeFlow}
                      onChange={() => activeVisibilityControls.toggle('cumulativeFlow')}
                    />
                    
                    {'showPartialCashOut' in activeVisibilityControls.visibility && (
                      <Toggle
                        label="Mostrar Retiro Parcial"
                        checked={activeVisibilityControls.visibility.showPartialCashOut}
                        onChange={() => activeVisibilityControls.toggle('showPartialCashOut')}
                      />
                    )}
                  </div>
                </div>
              )}

              {activeChart === 'combined' && activeVisibilityControls && (
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">Ajustes:</div>
                  <div className="space-y-3">
                    <Toggle
                      label="Mostrar Suma de Precio y Flujo"
                      checked={activeVisibilityControls.visibility.showSummedValue}
                      onChange={() => activeVisibilityControls.toggle('showSummedValue')}
                    />
                    
                    {'showPartialCashOut' in activeVisibilityControls.visibility && (
                      <Toggle
                        label="Mostrar Retiro Parcial"
                        checked={activeVisibilityControls.visibility.showPartialCashOut}
                        onChange={() => activeVisibilityControls.toggle('showPartialCashOut')}
                      />
                    )}
                  </div>
                </div>
              )}

              {activeChart === 'comparison' && activeVisibilityControls && (
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">Ajustes:</div>
                  <div className="space-y-3">
                    <Toggle
                      label="Mostrar Valores Reales"
                      checked={activeVisibilityControls.visibility.showReal}
                      onChange={() => activeVisibilityControls.toggle('showReal')}
                    />

                    {'showCustom' in activeVisibilityControls.visibility && (
                      <Toggle
                        label="Mostrar Inversión Personalizada"
                        checked={activeVisibilityControls.visibility.showCustom}
                        onChange={() => activeVisibilityControls.toggle('showCustom')}
                      />
                    )}
                  </div>
                </div>
              )}

              {(activeChart === 'percentages' || activeChart === 'amounts' || 
                activeChart === 'cumulativeRetirement' || activeChart === 'cumulativeReinvestment') && 
                activeVisibilityControls && activeVisibilityControls.visibility && (
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">Ajustes:</div>
                  <div className="space-y-3">
                    {activeChart === 'percentages' && 'showPercentages' in activeVisibilityControls.visibility && (
                      <Toggle
                        label="Mostrar Porcentajes"
                        checked={activeVisibilityControls.visibility.showPercentages}
                        onChange={() => activeVisibilityControls.toggle('showPercentages')}
                      />
                    )}
                    
                    {activeChart === 'amounts' && 'showAmounts' in activeVisibilityControls.visibility && (
                      <Toggle
                        label="Mostrar Montos"
                        checked={activeVisibilityControls.visibility.showAmounts}
                        onChange={() => activeVisibilityControls.toggle('showAmounts')}
                      />
                    )}
                    
                    {activeChart === 'cumulativeRetirement' && 'showCumulativeRetirement' in activeVisibilityControls.visibility && (
                      <Toggle
                        label="Mostrar Retiro Acumulado"
                        checked={activeVisibilityControls.visibility.showCumulativeRetirement}
                        onChange={() => activeVisibilityControls.toggle('showCumulativeRetirement')}
                      />
                    )}
                    
                    {activeChart === 'cumulativeReinvestment' && 'showCumulativeReinvestment' in activeVisibilityControls.visibility && (
                      <Toggle
                        label="Mostrar Reinversión Acumulada"
                        checked={activeVisibilityControls.visibility.showCumulativeReinvestment}
                        onChange={() => activeVisibilityControls.toggle('showCumulativeReinvestment')}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {additionalInfo[activeChart] && (
          <div className="mt-4">
            {additionalInfo[activeChart]}
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicChartSystem;